import type { IUseAccess } from 'hooks/useAccess';
import { useCloseContextMenuOnScroll } from 'hooks/useCloseContextMenuOnScroll';
import type { VariableIdType } from 'pages/Catalogs/Common/types';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { CatalogVariablesOtherRow } from '../../types';

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');
const getItemDisabled = ({ disabled }: { disabled?: boolean }) => Boolean(disabled);

type TCatalogVariablesOtherRowMenuProps = {
    row: CatalogVariablesOtherRow;
    onEdit: (id: VariableIdType) => () => void;
    onView: (id: number) => () => void;
    onRemove: (id: Exclude<VariableIdType, undefined>) => () => void;
    catalogsRules?: IUseAccess['catalogs'];
};

export const CatalogVariablesOtherRowMenu = ({
    row,
    onEdit,
    onView,
    onRemove,
    catalogsRules,
}: TCatalogVariablesOtherRowMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);

    useCloseContextMenuOnScroll({ setOpen: setIsOpen });

    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const items = useMemo(() => {
        const menuItems = [];

        if (catalogsRules?.canEdit) {
            menuItems.push({
                label: 'Редактировать',
                action: onEdit(row.id),
            });
        } else {
            menuItems.push({
                label: 'Просмотреть',
                action: onView(row.id),
            });
        }

        menuItems.push({
            label: 'Удалить',
            action: onRemove(row.id),
            disabled: !catalogsRules?.canDelete || !row.canDelete,
        });

        return menuItems;
    }, [catalogsRules?.canDelete, catalogsRules?.canEdit, onEdit, onRemove, onView, row.canDelete, row.id]);

    const handleItemOnClick = useCallback(
        (item: (typeof items)[number]) => () => {
            const action = getItemOnClick(item) as () => void;

            action();
            setIsOpen(false);
        },
        [],
    );

    return (
        <>
            <Button iconLeft={IconKebab} onlyIcon view="clear" size="xs" onClick={handleToggle} ref={buttonRef} />
            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                getItemOnClick={handleItemOnClick}
                getItemDisabled={getItemDisabled}
                anchorRef={buttonRef}
                direction="downStartLeft"
                onClickOutside={handleToggle}
            />
        </>
    );
};

CatalogVariablesOtherRowMenu.displayName = 'CatalogVariablesOtherRowMenu';
